"use client";

import { ArrowUpRight, Layers, Vote } from "lucide-react";
import { useTranslations } from "next-intl";
import BrandMark from "@/assets/logo.svg";
import { ConnectButton, LocaleSwitcher } from "@/components/core";
import { TopNavLogin } from "@/components/core/TopNav/TopNavLogin";
import { Link } from "@/i18n/navigation";
import styles from "./UniverseLanding.module.css";

const FEATURES = [
  { key: "investment", href: "/info/investment", Icon: Layers },
  { key: "dao", href: "/info/dao", Icon: Vote },
] as const;

export function UniverseLanding() {
  const t = useTranslations("universeLanding");
  const nav = useTranslations("nav");
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link href="/" className={styles.brand} aria-label="UniverseMeta">
          <BrandMark aria-hidden="true" />
          <span dir="ltr">
            Universe<span className={styles.brandAccent}>Meta</span>
          </span>
        </Link>
        <nav className={styles.navigation} aria-label={nav("menu")}>
          <Link href="/info/investment">{nav("investment")}</Link>
          <Link href="/info/dao">{nav("dao")}</Link>
          <Link href="/info">{nav("more")}</Link>
        </nav>
        <div className={styles.headerActions}>
          <LocaleSwitcher className={styles.languages} />
          <Link href="/profile/dashboard" className={styles.accountLink}>
            {t("account")} <ArrowUpRight size={15} />
          </Link>
          <TopNavLogin />
        </div>
      </header>
      <main>
        <section className={styles.hero}>
          <div className={styles.heroCopy}>
            <p className={styles.eyebrow}>
              <span />
              {t("eyebrow")}
            </p>
            <h1>
              <span>{t("title")}</span>
            </h1>
            <p className={styles.heroDescription}>{t("description")}</p>
            <div className={styles.ctas}>
              <Link href="/profile/dashboard" className={styles.primary}>
                {t("start")}
                <ArrowUpRight size={20} />
              </Link>
              <Link href="#possibilities" className={styles.secondary}>
                {t("explore")}
                <span aria-hidden="true">↓</span>
              </Link>
            </div>
          </div>
          <div className={styles.visual} aria-hidden="true">
            <div className={styles.starfield} />
            <div className={styles.outerOrbit} />
            <div className={styles.middleOrbit} />
            <div className={styles.sphere}>
              <div className={styles.sphereLines} />
            </div>
            <div className={styles.frontOrbit} />
            <span className={styles.moon} />
            <span className={styles.star}>✦</span>
            <div className={`${styles.orbitLabel} ${styles.labelOne}`}>
              <Layers size={16} />
              {t("investment.label")}
            </div>
            <div className={`${styles.orbitLabel} ${styles.labelTwo}`}>
              <Vote size={16} />
              {t("dao.label")}
            </div>
            <div className={styles.visualCaption}>
              <span />
              UNIVERSEMETA
            </div>
          </div>
          <div className={styles.heroBottom}>
            <span>01 / UNIVERSEMETA</span>
            <span>{t("scroll")} ↓</span>
          </div>
        </section>
        <section className={styles.possibilities} id="possibilities">
          <div className={styles.sectionHeading}>
            <div>
              <p className={styles.eyebrow}>{t("possibilitiesEyebrow")}</p>
              <h2>{t("possibilitiesTitle")}</h2>
            </div>
            <p>{t("possibilitiesDescription")}</p>
          </div>
          <div className={styles.featureGrid}>
            {FEATURES.map(({ key, href, Icon }, index) => (
              <Link href={href} className={styles.featureCard} key={key}>
                <div className={styles.cardTop}>
                  <Icon size={26} strokeWidth={1.4} />
                  <span>0{index + 1}</span>
                </div>
                <h3>{t(`${key}.label`)}</h3>
                <p>{t(`${key}.description`)}</p>
                <span className={styles.cardLink}>
                  {t("discover")}
                  <ArrowUpRight size={19} />
                </span>
              </Link>
            ))}
          </div>
        </section>
        <section className={styles.closing}>
          <p className={styles.eyebrow}>{t("closingEyebrow")}</p>
          <h2>{t("closingTitle")}</h2>
          <p>{t("closingDescription")}</p>
          <div className={styles.connect}>
            <ConnectButton>{nav("connectWallet")}</ConnectButton>
            <Link href="/profile/dashboard" className={styles.secondary}>
              {t("account")}
              <ArrowUpRight size={18} />
            </Link>
          </div>
        </section>
      </main>
      <footer className={styles.footer}>
        <Link href="/" className={styles.brand}>
          <BrandMark aria-hidden="true" />
          <span dir="ltr">UniverseMeta</span>
        </Link>
        <p>{t("copyright", { year: new Date().getFullYear() })}</p>
        <div>
          <Link href="/profile/dashboard">{t("account")}</Link>
          <Link href="/info">{nav("more")}</Link>
          <Link href="/info/guide">{nav("guide")}</Link>
        </div>
      </footer>
    </div>
  );
}
